import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Save, ArrowLeft } from 'lucide-react';
import api, { formatCurrency } from '../services/api';

export default function CollectionMemoForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;
  const [saving, setSaving] = useState(false);
  const [autoRound, setAutoRound] = useState(true);
  const [form, setForm] = useState({
    template: 'template1', date: new Date().toISOString().split('T')[0],
    collection_no: '', party_name: '', attention: '', truck_no: '',
    from_location: '', to_location: '', freight: '', advance: '',
    weight: '', guarantee: '', payable_at: '',
    extra_height: '', detention_per_day: '', loading_capacity: '',
    bank_account: 'yes_bank', pan: '',
    terms: '', notes: '', round_off_manual: '',
  });

  useEffect(() => {
    if (isEdit) {
      api.get(`/collection-memos/${id}`).then(r => {
        const d = r.data;
        setForm({
          template: d.template || 'template1', date: d.date || '',
          collection_no: d.collection_no || '', party_name: d.party_name || '',
          attention: d.attention || '', truck_no: d.truck_no || '',
          from_location: d.from_location || '', to_location: d.to_location || '',
          freight: d.freight || '', advance: d.advance || '',
          weight: d.weight || '', guarantee: d.guarantee || '',
          payable_at: d.payable_at || '',
          extra_height: d.extra_height || '', detention_per_day: d.detention_per_day || '',
          loading_capacity: d.loading_capacity || '',
          bank_account: d.bank_account || 'yes_bank', pan: d.pan || '',
          terms: d.terms || '', notes: d.notes || '',
          round_off_manual: d.round_off !== undefined ? d.round_off : '',
        });
        if (d.round_off !== undefined && d.round_off !== 0) setAutoRound(false);
      }).catch(() => { toast.error('Not found'); navigate('/collection-memos'); });
    }
  }, [id, isEdit, navigate]);

  const u = (f, v) => setForm(s => ({ ...s, [f]: v }));
  const freight = parseFloat(form.freight) || 0;
  const advance = parseFloat(form.advance) || 0;
  const balance = freight - advance;
  const autoRoundVal = Math.round(Math.round(balance) - balance * 100) / 100;
  const roundOff = autoRound ? (Math.round(balance * 100) % 100 === 0 ? 0 : parseFloat((Math.round(balance) - balance).toFixed(2))) : (parseFloat(form.round_off_manual) || 0);
  const totalPayable = Math.round((balance + roundOff) * 100) / 100;

  const handleSave = async () => {
    if (!form.party_name || !form.truck_no) { toast.error('Fill party name and truck no.'); return; }
    setSaving(true);
    const payload = { ...form, round_off_manual: autoRound ? null : parseFloat(form.round_off_manual) || 0 };
    try {
      if (isEdit) {
        await api.put(`/collection-memos/${id}`, payload);
        toast.success('Updated'); navigate(`/collection-memos/${id}`);
      } else {
        const res = await api.post('/collection-memos', payload);
        toast.success('Created'); navigate(`/collection-memos/${res.data.memo_id}`);
      }
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  const handleOCR = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    toast.info('Extracting data from PDF...');
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await api.post('/collection-memos/ocr', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      const d = res.data;
      if (d.truck_no) u('truck_no', d.truck_no);
      if (d.from_location) u('from_location', d.from_location);
      if (d.to_location) u('to_location', d.to_location);
      if (d.freight) u('freight', d.freight);
      if (d.advance) u('advance', d.advance);
      if (d.party_name) u('party_name', d.party_name);
      if (d.date) u('date', d.date);
      toast.success('Data extracted from PDF');
    } catch { toast.error('OCR extraction failed'); }
    e.target.value = '';
  };

  return (
    <div className="max-w-4xl mx-auto space-y-5" data-testid="cm-form">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/collection-memos')} className="p-2 hover:bg-slate-100 rounded-md"><ArrowLeft className="w-5 h-5" /></button>
          <h1 className="font-heading font-bold text-2xl text-slate-900">{isEdit ? 'Edit' : 'New'} Collection Memo</h1>
        </div>
        <div className="flex gap-2">
          <label className="btn-secondary cursor-pointer flex items-center gap-2 text-xs">
            Upload PDF (OCR)
            <input type="file" accept=".pdf" onChange={handleOCR} className="hidden" />
          </label>
          <button onClick={handleSave} disabled={saving} className="btn-accent flex items-center gap-2" data-testid="save-cm-btn">
            <Save className="w-4 h-4" /> {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-md p-5 space-y-5">
        <div className="flex items-center gap-4">
          <label className="form-label mb-0">Template:</label>
          <select value={form.template} onChange={e => u('template', e.target.value)} className="form-input w-48" data-testid="cm-template">
            <option value="template1">Template 1 - MSP Style</option>
            <option value="template2">Template 2 - A&A Style</option>
          </select>
        </div>

        <h3 className="font-heading font-semibold text-slate-700 text-sm uppercase tracking-wider">Basic Details</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <div><label className="form-label">Date</label><input type="date" value={form.date} onChange={e => u('date', e.target.value)} className="form-input" /></div>
          <div><label className="form-label">Collection No.</label><input type="text" value={form.collection_no} onChange={e => u('collection_no', e.target.value)} className="form-input" /></div>
          <div><label className="form-label">Party Name</label><input type="text" value={form.party_name} onChange={e => u('party_name', e.target.value)} className="form-input" data-testid="cm-party" /></div>
          <div><label className="form-label">Attention</label><input type="text" value={form.attention} onChange={e => u('attention', e.target.value)} className="form-input" /></div>
          <div><label className="form-label">Truck No.</label><input type="text" value={form.truck_no} onChange={e => u('truck_no', e.target.value)} className="form-input" placeholder="DL 01 AB 1234" data-testid="cm-truck" /></div>
          <div><label className="form-label">From</label><input type="text" value={form.from_location} onChange={e => u('from_location', e.target.value)} className="form-input" data-testid="cm-from" /></div>
          <div><label className="form-label">To</label><input type="text" value={form.to_location} onChange={e => u('to_location', e.target.value)} className="form-input" data-testid="cm-to" /></div>
          <div><label className="form-label">Weight</label><input type="text" value={form.weight} onChange={e => u('weight', e.target.value)} className="form-input" /></div>
        </div>

        <h3 className="font-heading font-semibold text-slate-700 text-sm uppercase tracking-wider">Financials</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <div><label className="form-label">Freight (Rs.)</label><input type="number" value={form.freight} onChange={e => u('freight', e.target.value)} className="form-input font-mono text-right" data-testid="cm-freight" /></div>
          <div><label className="form-label">Advance (Rs.)</label><input type="number" value={form.advance} onChange={e => u('advance', e.target.value)} className="form-input font-mono text-right" data-testid="cm-advance" /></div>
          <div className="bg-green-50 rounded-md p-3 flex flex-col justify-center"><p className="text-xs text-slate-500">Balance</p><p className="font-mono font-bold text-lg text-green-600" data-testid="cm-balance">Rs. {formatCurrency(balance)}</p></div>
        </div>
        <div className="flex items-center gap-4 bg-slate-50 rounded-md p-3">
          <div className="flex items-center gap-2">
            <input type="checkbox" checked={autoRound} onChange={e => setAutoRound(e.target.checked)} id="autoRound" className="w-4 h-4" />
            <label htmlFor="autoRound" className="text-sm text-slate-600">Auto Round Off</label>
          </div>
          {!autoRound && <div><label className="form-label mb-0 mr-2">Round Off (+/-)</label><input type="number" step="0.01" value={form.round_off_manual} onChange={e => u('round_off_manual', e.target.value)} className="form-input w-32 font-mono text-right inline-block" data-testid="cm-roundoff" /></div>}
          <div><p className="text-xs text-slate-500">Round Off: <span className="font-mono font-bold">{roundOff >= 0 ? '+' : ''}{roundOff.toFixed(2)}</span></p></div>
          <div className="ml-auto"><p className="text-sm text-slate-600">Total Payable: <span className="font-mono font-bold text-lg" data-testid="cm-total">Rs. {formatCurrency(totalPayable)}</span></p></div>
        </div>

        {form.template === 'template1' && (<>
          <h3 className="font-heading font-semibold text-slate-700 text-sm uppercase tracking-wider">Template 1 Fields (MSP Style)</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <div><label className="form-label">Guarantee</label><input type="text" value={form.guarantee} onChange={e => u('guarantee', e.target.value)} className="form-input" /></div>
            <div><label className="form-label">Payable At</label><input type="text" value={form.payable_at} onChange={e => u('payable_at', e.target.value)} className="form-input" /></div>
            <div><label className="form-label">Extra Height</label><input type="text" value={form.extra_height} onChange={e => u('extra_height', e.target.value)} className="form-input" /></div>
            <div><label className="form-label">Detention Per Day</label><input type="text" value={form.detention_per_day} onChange={e => u('detention_per_day', e.target.value)} className="form-input" /></div>
          </div>
        </>)}
        {form.template === 'template2' && (<>
          <h3 className="font-heading font-semibold text-slate-700 text-sm uppercase tracking-wider">Template 2 Fields (A&A Style)</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <div><label className="form-label">Loading Capacity</label><input type="text" value={form.loading_capacity} onChange={e => u('loading_capacity', e.target.value)} className="form-input" /></div>
          </div>
        </>)}

        <h3 className="font-heading font-semibold text-slate-700 text-sm uppercase tracking-wider">Bank & Other</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <div><label className="form-label">Bank Account</label><select value={form.bank_account} onChange={e => u('bank_account', e.target.value)} className="form-input"><option value="">No Bank Details</option><option value="yes_bank">Yes Bank</option><option value="axis_bank">Axis Bank</option></select></div>
          <div><label className="form-label">PAN</label><input type="text" value={form.pan} onChange={e => u('pan', e.target.value)} className="form-input" placeholder="ABCDE1234F" /></div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div><label className="form-label">Terms</label><textarea value={form.terms} onChange={e => u('terms', e.target.value)} className="form-input h-20" /></div>
          <div><label className="form-label">Notes</label><textarea value={form.notes} onChange={e => u('notes', e.target.value)} className="form-input h-20" /></div>
        </div>
      </div>
    </div>
  );
}
